/**
 * 화학물질 검색 팝업 (GetWorBasic02R2)
 * - 유해물질 국문명/영문명/CAS No 검색, 다중 선택 후 기본정보 그리드에 삽입
 * - TWA·STEL 둘 다 있는 경우 "TWA 300ppm / STEL 500ppm"으로 삽입
 * - 디자인: WorkTypeItemManagement 팝업과 동일 (흰색 헤더, 회색 배경, 파란 버튼)
 */
import React, { useState, useRef, useEffect } from 'react';
import { Dialog } from '../../../../Components/MUIs';
import { AgGridReact } from 'ag-grid-react';
import {
  Box,
  TextField,
  InputAdornment,
  Typography,
  Button,
  Paper,
  IconButton,
  Button as MuiButton,
} from '@mui/material';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useSearch1 } from './WOR_BASIC_02_QUERY';

const TWA_STEL_FIXED_VALUE = 'TWA 300ppm / STEL 500ppm';

const columnDefs = [
  { field: 'no', headerName: 'No', width: 80, checkboxSelection: true, headerCheckboxSelection: true },
  { field: 'korNm', headerName: '유해물질 국문명', flex: 1, minWidth: 120 },
  { field: 'engNm', headerName: '유해물질 영문명', flex: 1, minWidth: 120 },
  { field: 'casNo', headerName: 'CAS 번호', width: 120 },
  { field: 'twaVal', headerName: 'TWA', width: 100 },
  { field: 'stelVal', headerName: 'STEL', width: 100 },
  { field: 'remark', headerName: '비고', flex: 1, minWidth: 120 },
];

/**
 * 선택한 화학물질 행을 기본정보 그리드 행 형태로 변환
 * TWA·STEL 둘 다 있으면 "TWA 300ppm / STEL 500ppm" 적용
 */
export function toMainGridRow(row, index) {
  const hasTwa = row.twaVal != null && String(row.twaVal).trim() !== '';
  const hasStel = row.stelVal != null && String(row.stelVal).trim() !== '';
  const twaVal = hasTwa && hasStel ? TWA_STEL_FIXED_VALUE : (row.twaVal ?? row.stelVal ?? '');
  const stelVal = row.stelVal ?? '';
  return {
    no: `new_${Date.now()}_${index}`,
    chemSeq: row.chemSeq,
    korNm: row.korNm ?? '',
    twaVal,
    stelVal,
    remark: row.remark ?? '',
    newYn: 'Y',
    editYn: 'Y',
  };
}

function ChemSearchModal({ open, onClose, onSelect }) {
  const [searchWord, setSearchWord] = useState('');
  const [searchTrigger, setSearchTrigger] = useState('');
  const gridRef = useRef(null);

  const { chemList, isFetching } = useSearch1(searchTrigger, { enabled: open });

  useEffect(() => {
    if (open) setSearchTrigger('');
  }, [open]);

  const onSearch = () => {
    setSearchTrigger(searchWord);
  };

  const handleSelect = () => {
    const api = gridRef.current?.api;
    const selected = api?.getSelectedRows() ?? [];
    const mapped = selected.map((row, idx) => toMainGridRow(row, idx));
    onSelect?.(mapped);
    onClose?.();
  };

  const header = (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        whiteSpace: 'nowrap',
        borderBottom: '1px solid #f0f2f4',
        bgcolor: 'white',
        px: 3,
        py: 1.5,
        flexShrink: 0,
        zIndex: 20,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: '#111418', flex: 1 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(19, 127, 236, 0.1)',
            color: 'primary.main',
            borderRadius: 1.5,
          }}
        >
          <SearchIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{
              color: '#111418',
              fontSize: 22,
              fontWeight: 'bold',
              letterSpacing: -0.15,
              mb: 0.5,
            }}
          >
            화학물질 검색
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#617589',
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            유해물질 국문명, 영문명, CAS 번호로 검색합니다
          </Typography>
        </Box>
        <TextField
          placeholder="검색어 입력"
          size="small"
          variant="outlined"
          value={searchWord}
          onChange={(e) => setSearchWord(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), onSearch())}
          sx={{
            minWidth: 220,
            '& .MuiOutlinedInput-root': {
              paddingRight: 0,
              backgroundColor: '#fff',
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ height: '100%' }}>
                <MuiButton
                  edge="end"
                  size="small"
                  onClick={onSearch}
                  sx={{
                    borderLeft: 1,
                    borderColor: 'divider',
                    color: 'rgba(0, 0, 0, 0.54)',
                    minWidth: 40,
                    borderRadius: 0,
                    borderTopRightRadius: 8,
                    borderBottomRightRadius: 8,
                  }}
                >
                  <SearchIcon sx={{ m: 0 }} />
                </MuiButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <IconButton
        onClick={onClose}
        size="small"
        sx={{
          color: '#617589',
          '&:hover': {
            color: '#111418',
            bgcolor: 'transparent',
          },
        }}
      >
        <CloseIcon />
      </IconButton>
    </Paper>
  );

  const footer = (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 1.5,
        px: 3,
        py: 2,
        borderTop: '1px solid #f0f2f4',
        bgcolor: 'white',
        flexShrink: 0,
      }}
    >
      <Button
        onClick={onClose}
        variant="outlined"
        sx={{
          minWidth: '100px',
          color: '#617589',
          borderColor: '#e5e7eb',
          fontWeight: 500,
          '&:hover': {
            borderColor: '#d1d5db',
            bgcolor: 'transparent',
          },
        }}
      >
        취소
      </Button>
      <Button
        onClick={handleSelect}
        variant="contained"
        sx={{
          minWidth: '100px',
          fontWeight: 500,
          backgroundColor: '#1976d2',
          boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
          '&:hover': {
            backgroundColor: '#1565c0',
            boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
          },
        }}
      >
        선택
      </Button>
    </Paper>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={header}
      titleSx={{ p: 0 }}
      maxWidth="lg"
      fullWidth
      useCloseButton={false}
      useSaveButton={false}
      actions={footer}
      actionsSx={{ p: 0 }}
      PaperProps={{
        sx: {
          borderRadius: 0,
          overflow: 'hidden',
          maxHeight: '90vh',
          height: 'auto',
        },
      }}
      contentSx={{
        display: 'flex',
        flexDirection: 'column',
        p: 0,
        overflow: 'hidden',
        maxHeight: '90vh',
      }}
      content={
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            bgcolor: '#f6f7f8',
            p: 2,
            '&::-webkit-scrollbar': { width: '8px' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1' },
          }}
        >
          <Box className="ag-theme-alpine no-scroll" sx={{ height: 420, width: '100%' }}>
            <AgGridReact
              ref={gridRef}
              rowData={chemList}
              columnDefs={columnDefs}
              getRowId={(params) => String(params.data.chemSeq ?? params.data.no)}
              rowSelection="multiple"
              suppressRowClickSelection
              defaultColDef={{ resizable: true, sortable: true }}
              loading={isFetching}
            />
          </Box>
        </Box>
      }
    />
  );
}

export default ChemSearchModal;
